import authService, { ClearAccessExpiresAt, GetAccessExpiresAt, SetAccessExpiresAt } from './authService';
import { CreateLogger } from './logger';

const logger = CreateLogger('services/api');
const RAW_API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:9999';
const ACCESS_TOKEN_KEY = 'access_token';
const LEGACY_TOKEN_KEY = 'token';

function resolveApiBaseUrl(rawBaseUrl) {
    const sanitized = String(rawBaseUrl || '')
        .trim()
        .replace(/\/+$/, '');

    if (!sanitized) {
        return 'http://localhost:9999/api/v2';
    }

    // Base RELATIVA (ex.: "/api/v2"): a API responde na MESMA origem da página
    // (proxy do dev server / reverse proxy). Essencial para acesso via
    // Tailscale compartilhado — o IP da máquina é OUTRO na rede do convidado,
    // então URL absoluta com IP nunca funcionaria para ele.
    if (sanitized.startsWith('/')) {
        return sanitized.endsWith('/api/v2')
            ? sanitized
            : `${sanitized.replace(/\/$/, '')}/api/v2`.replace('//api/v2', '/api/v2');
    }

    if (sanitized.endsWith('/api/v2')) {
        return sanitized;
    }

    if (sanitized.endsWith('/api')) {
        return `${sanitized}/v2`;
    }

    return `${sanitized}/api/v2`;
}

function normalizeEndpoint(endpoint) {
    const raw = String(endpoint || '').trim();
    if (!raw) {
        return '/';
    }

    const withLeadingSlash = raw.startsWith('/') ? raw : `/${raw}`;
    const withoutV2Prefix = withLeadingSlash.replace(/^\/api\/v2(?=\/|$)/, '');
    const withoutLegacyPrefix = withoutV2Prefix.replace(/^\/api(?=\/|$)/, '');

    return withoutLegacyPrefix || '/';
}

const API_BASE_URL = resolveApiBaseUrl(RAW_API_BASE_URL);

// ── Padrão melidash: "o backend está no ar?" ────────────────────────────────
// Mensagem ÚNICA para backend fora do ar (decisão do dono, revisão do M1) —
// toda tela que exibir erro de carga acaba mostrando `error.message`, então a
// padronização vive aqui, na origem.
export const BACKEND_OFFLINE_MESSAGE =
    'Servidor do melidash fora do ar. Verifique se o backend está rodando e tente novamente.';

// Sonda a rota de liveness (GET /health/live — pública, mesma que o bot
// third-party usa). Devolve true/false; null quando a sonda não conclui
// (ex.: abortada). Cache curto para uma página com N cards quebrando junto
// não disparar N sondas.
const LIVENESS_PROBE_CACHE_MS = 10000;
const LIVENESS_PROBE_TIMEOUT_MS = 3000;
let livenessProbeCache = { at: 0, alive: null };

async function ProbeBackendAlive() {
    const now = Date.now();
    if (now - livenessProbeCache.at < LIVENESS_PROBE_CACHE_MS && livenessProbeCache.alive !== null) {
        return livenessProbeCache.alive;
    }

    try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), LIVENESS_PROBE_TIMEOUT_MS);
        const response = await fetch(`${API_BASE_URL}/health/live`, { signal: controller.signal });
        clearTimeout(timer);
        livenessProbeCache = { at: now, alive: response.ok };
    } catch {
        livenessProbeCache = { at: now, alive: false };
    }
    return livenessProbeCache.alive;
}

function createRequestId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }

    return `req-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
}

class APIError extends Error {
    constructor(message, code, status, details = null) {
        super(message);
        this.name = 'APIError';
        this.code = code;
        this.status = status;
        this.details = details;
        this.response = {
            status,
            data: {
                success: false,
                data: null,
                error: {
                    code,
                    message,
                    details,
                },
            },
        };
    }
}

const GetToken = () => localStorage.getItem(ACCESS_TOKEN_KEY) || localStorage.getItem(LEGACY_TOKEN_KEY);
const SetToken = (token) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
};
const RemoveToken = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
};

function normalizePayload(payload) {
    if (payload && typeof payload === 'object' && 'success' in payload && 'data' in payload) {
        const normalizedData = payload.data;

        if (normalizedData && typeof normalizedData === 'object') {
            try {
                // Backward-compatibility bridge:
                // - old callers expect envelope: response.success / response.data
                // - new callers expect unwrapped data directly
                Object.defineProperty(normalizedData, 'success', {
                    value: payload.success,
                    configurable: true,
                    enumerable: false,
                });
                Object.defineProperty(normalizedData, 'error', {
                    value: payload.error ?? null,
                    configurable: true,
                    enumerable: false,
                });
                Object.defineProperty(normalizedData, 'meta', {
                    value: payload.meta ?? null,
                    configurable: true,
                    enumerable: false,
                });
                Object.defineProperty(normalizedData, 'data', {
                    value: normalizedData,
                    configurable: true,
                    enumerable: false,
                });
            } catch {
                // If object is sealed/frozen, fallback to lightweight wrapper
                return {
                    success: payload.success,
                    data: normalizedData,
                    error: payload.error ?? null,
                    meta: payload.meta ?? null,
                };
            }
        }

        return normalizedData;
    }
    return payload;
}

function parseErrorPayload(payload, fallbackStatus) {
    if (payload?.error) {
        if (typeof payload.error === 'string') {
            return {
                message: payload.error,
                code: 'UNKNOWN_ERROR',
                details: null,
                status: fallbackStatus,
            };
        }

        return {
            message: payload.error.message || 'Unknown error',
            code: payload.error.code || 'UNKNOWN_ERROR',
            details: payload.error.details || null,
            status: fallbackStatus,
        };
    }

    return {
        message: payload?.detail || payload?.message || 'Unknown error',
        code: payload?.code || 'UNKNOWN_ERROR',
        details: null,
        status: fallbackStatus,
    };
}

// Margem antes do exp pra disparar refresh proativo. 30s cobre latency da
// rede + clock skew típico sem ficar disparando refresh em cada request.
const REFRESH_AHEAD_MS = 30_000;

// Mutex: se vários requests detectarem expiração simultânea, só UMA chamada
// /auth/refresh acontece. Os demais aguardam o mesmo promise. Reset pra null
// no finally (sucesso ou falha) — falha não significa retry imediato.
let refreshPromise = null;

/**
 * Chama POST /auth/refresh. O refresh token vai automático via cookie HttpOnly
 * (browser anexa porque credentials='include'). Resposta traz novo access +
 * cookie do novo refresh já trocado pelo Set-Cookie do server.
 *
 * IMPORTANTE: não usa fetchAPI() pra evitar loop infinito (fetchAPI tem este
 * próprio refresh embutido). É um fetch raw.
 */
async function refreshAccessToken() {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
        try {
            const resp = await fetch(`${API_BASE_URL}/auth/refresh`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'x-request-id': createRequestId() },
            });
            if (!resp.ok) {
                throw new APIError('Refresh failed', 'REFRESH_FAILED', resp.status);
            }
            const payload = await resp.json().catch(() => null);
            const data = normalizePayload(payload);
            const newToken = data?.access_token;
            const expiresIn = Number(data?.expires_in);
            if (!newToken) {
                throw new APIError('Refresh missing access_token', 'REFRESH_FAILED', 500);
            }
            SetToken(newToken);
            if (Number.isFinite(expiresIn) && expiresIn > 0) {
                SetAccessExpiresAt(Date.now() + expiresIn * 1000);
            } else {
                ClearAccessExpiresAt();
            }
            return newToken;
        } finally {
            refreshPromise = null;
        }
    })();
    return refreshPromise;
}

function shouldProactivelyRefresh(token) {
    if (!token) return false;
    const expiresAt = GetAccessExpiresAt();
    if (!expiresAt) return false;
    return Date.now() >= expiresAt - REFRESH_AHEAD_MS;
}

/**
 * Query string a partir de um objeto, DESCARTANDO o que está vazio.
 *
 * ⚠️ Existe por causa de um defeito real: `new URLSearchParams({status: undefined})`
 * não omite a chave — ele serializa a STRING `"undefined"`, e o zod do backend
 * recusa com 400. O sintoma é cruel: a tela abre quebrada só quando o usuário
 * NÃO escolheu filtro nenhum (que é o caso mais comum), e funciona assim que ele
 * escolhe qualquer um. Foi medido em duas telas do M8 antes de virar helper.
 *
 * `null` e string vazia caem pela mesma razão: "sem filtro" é a AUSÊNCIA da
 * chave, não uma chave com valor vazio.
 */
function BuildQuery(params = {}) {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params ?? {})) {
        if (value !== undefined && value !== null && value !== '') {
            search.append(key, String(value));
        }
    }
    return search.toString();
}

/**
 * Filtros multi-valor viajam como CSV (`?tags=a,b` — o zod do backend faz o
 * split). Aceita array (junta) ou string já pronta; vazio some da query.
 */
function joinCsvParams(params = {}, keys = []) {
    const out = { ...(params ?? {}) };
    for (const key of keys) {
        if (Array.isArray(out[key])) {
            out[key] = out[key].filter((v) => v !== undefined && v !== null && v !== '').join(',');
        }
    }
    return out;
}

const fetchAPI = async (endpoint, options = {}) => {
    const normalizedEndpoint = normalizeEndpoint(endpoint);
    // Don't run refresh logic on the refresh endpoint itself — avoids recursion
    // se um caller usar fetchAPI('/auth/refresh') (não deveríamos, mas defesa).
    const isAuthRefresh = normalizedEndpoint === '/auth/refresh';
    // O LOGIN é o request que ESTABELECE a sessão: um 401 aqui é veredito de
    // credencial ("senha errada"), nunca sessão vencida. Sem esta distinção o
    // caminho de refresh abaixo tentava renovar uma sessão que não existe, o
    // refresh falhava e a mensagem do servidor era substituída por "Sessao
    // expirada" — a pessoa errava a senha e lia que a sessão tinha expirado.
    const isLoginAttempt = normalizedEndpoint === '/auth';
    const { _isRetry, ...fetchOptions } = options;

    try {
        // Proactive refresh: access prestes a expirar → renova ANTES da request.
        // Sem isso o request iria com token vencido e voltaria 401 → roundtrip
        // desnecessário (e em endpoints longos, latência percebida).
        if (!isAuthRefresh && !isLoginAttempt && !_isRetry && shouldProactivelyRefresh(GetToken())) {
            try {
                await refreshAccessToken();
            } catch {
                // Falha aqui não é fatal — segue com token velho. O 401
                // subsequente vai disparar o caminho reativo de refresh.
            }
        }

        const token = GetToken();
        const headers = {
            ...fetchOptions.headers,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'x-request-id': createRequestId(),
        };

        // Só envia Content-Type quando há body JSON. Em GETs sem Content-Type custom,
        // evita o preflight CORS OPTIONS extra que o browser dispara.
        if (fetchOptions.body != null && !(fetchOptions.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        const response = await fetch(`${API_BASE_URL}${normalizedEndpoint}`, {
            ...fetchOptions,
            // credentials: 'include' faz o browser enviar cookies (refresh token
            // HttpOnly) em requests cross-origin. Cookie tem Path=/api/v2/auth
            // então só vai em endpoints de auth — outras rotas não recebem.
            credentials: 'include',
            headers,
        });

        let payload = null;
        if (response.status !== 204) {
            try {
                payload = await response.json();
            } catch {
                payload = null;
            }
        }

        if (!response.ok) {
            const parsedError = payload
                ? parseErrorPayload(payload, response.status)
                : {
                      message:
                          response.status === 413
                              ? 'Arquivo muito grande para o servidor'
                              : response.statusText || `HTTP ${response.status}`,
                      code: response.status === 413 ? 'PAYLOAD_TOO_LARGE' : 'HTTP_ERROR',
                      details: null,
                      status: response.status,
                  };

            if (response.status === 401) {
                if (parsedError.code === 'REAUTH_REQUIRED') {
                    throw new APIError(parsedError.message, parsedError.code, 401, parsedError.details);
                }

                // Login recusado: propaga a mensagem do servidor como ela é.
                // Não há sessão para renovar nem token para limpar, e avisar
                // "sessão expirada" na tela de login é ruído — quem está ali
                // precisa saber que o e-mail ou a senha está errado.
                if (isLoginAttempt) {
                    throw new APIError(parsedError.message, parsedError.code, 401, parsedError.details);
                }

                // Refresh reativo: 401 num request que NÃO é /auth/refresh e
                // NÃO é retry — tenta refrescar e refazer 1x. Se refresh falhar
                // (expirado, revogado, etc), aí sim notifica "Sessão expirada".
                if (!isAuthRefresh && !_isRetry) {
                    try {
                        await refreshAccessToken();
                        return await fetchAPI(endpoint, { ...options, _isRetry: true });
                    } catch {
                        // Refresh falhou → sessão realmente expirou. Cai pro
                        // tratamento padrão de 401 abaixo.
                    }
                }

                RemoveToken();
                ClearAccessExpiresAt();
                authService.notifyTokenExpired();
                throw new APIError('Sessao expirada', 'TOKEN_EXPIRED', 401, parsedError.details);
            }

            throw new APIError(parsedError.message, parsedError.code, parsedError.status, parsedError.details);
        }

        if (response.status === 204) {
            return null;
        }

        return normalizePayload(payload);
    } catch (error) {
        // Cancelamento pelo caller (`options.signal` abortado) NÃO é erro de
        // rede: a tela trocou de página/termo e descartou esta resposta de
        // propósito. Relança intacto ANTES da sonda de liveness e da conversão
        // em APIError — senão cada abort viraria "Erro de conexao" na tela e
        // um probe desnecessário no backend.
        if (error?.name === 'AbortError') {
            throw error;
        }

        if (error instanceof APIError) {
            throw error;
        }

        logger.error('network_error', {
            endpoint: normalizedEndpoint,
            error: { name: error.name, message: error.message },
        });

        // Erro de REDE (fetch nem chegou a resposta): antes de culpar o
        // backend inteiro, sonda a rota de liveness (a MESMA que o bot
        // third-party valida a cada 5 min). Fora do ar de verdade → mensagem
        // padrão ÚNICA do melidash; liveness OK → o problema é desta rota.
        const backendAlive = await ProbeBackendAlive();
        if (backendAlive === false) {
            const offlineError = new APIError(BACKEND_OFFLINE_MESSAGE, 'BACKEND_OFFLINE', 0);
            offlineError.offline = true;
            throw offlineError;
        }
        throw new APIError(
            backendAlive === true
                ? 'O servidor está no ar, mas esta funcionalidade não respondeu. Tente novamente; se persistir, reporte a rota.'
                : 'Erro de conexao',
            'NETWORK_ERROR',
            0,
        );
    }
};

const api = {
    get: (endpoint, options = {}) => fetchAPI(endpoint, options),
    post: (endpoint, data) =>
        fetchAPI(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    put: (endpoint, data) =>
        fetchAPI(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),
    delete: (endpoint) => fetchAPI(endpoint, { method: 'DELETE' }),

    login: (email, password) =>
        fetchAPI('/auth', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        }),
    logout: () => fetchAPI('/auth/logout', { method: 'POST' }),
    getCurrentUser: () => fetchAPI('/auth/me'),

    getProfile: () => fetchAPI('/profile'),
    updateProfile: (data) => fetchAPI('/profile', { method: 'PUT', body: JSON.stringify(data) }),
    updatePassword: (data) => fetchAPI('/profile/password', { method: 'PUT', body: JSON.stringify(data) }),
    getProfileLoginHistory: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/profile/login-history${qs ? `?${qs}` : ''}`);
    },

    // ── MCP — observabilidade (conexões, histórico, resumo, revogar) ────────
    getUsers: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/users${qs ? `?${qs}` : ''}`);
    },
    createUser: (data) => fetchAPI('/users', { method: 'POST', body: JSON.stringify(data) }),
    getUser: (id) => fetchAPI(`/users/${id}`),
    getUserLoginHistory: (id, params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/users/${id}/login-history${qs ? `?${qs}` : ''}`);
    },
    updateUser: (id, data) => fetchAPI(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteUser: (id) => fetchAPI(`/users/${id}`, { method: 'DELETE' }),
    getPermissions: () => fetchAPI('/permissions'),
    getCustomers: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/customers${qs ? `?${qs}` : ''}`);
    },
    getCustomer: (buyerId, params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/customers/${buyerId}${qs ? `?${qs}` : ''}`);
    },

    getReputationIssues: () => fetchAPI('/dashboard/reputation-issues'),

    // Campaigns
    getCampaigns: () => fetchAPI('/campaigns'),
    // Auditoria de campanhas (20/08): histórico de mudanças por anúncio (com o
    // recorte de divergência de opt-in) e os bônus (cashback) do extrato.
    getCampaignItemEvents: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/campaigns/item-events${qs ? `?${qs}` : ''}`);
    },
    getCampaignBonuses: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/campaigns/bonuses${qs ? `?${qs}` : ''}`);
    },
    getCampaign: (id) => fetchAPI(`/campaigns/${id}`),
    getCampaignStats: () => fetchAPI('/campaigns/overview/stats'),
    // Resumo da Visão geral: estado ATUAL dos anúncios ativos (rebate por
    // unidade da campanha vigente, campanhas em andamento, fila do Aplique
    // agora). Sem query params: o universo é sempre o dos anúncios ativos.
    getCampaignOverviewSummary: () => fetchAPI('/campaigns/overview/summary'),
    getActiveItems: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/campaigns/items/active${qs ? `?${qs}` : ''}`);
    },
    getItemsNeedingAttention: () => fetchAPI('/campaigns/items/needs-attention'),
    // Página de detalhe do anúncio: UMA linha do monitor + séries históricas.
    getMonitorItem: (itemId) => fetchAPI(`/campaigns/items/${encodeURIComponent(itemId)}`),
    markItemCampaignsSeen: (itemId) =>
        fetchAPI(`/campaigns/items/${encodeURIComponent(itemId)}/campaigns/seen`, { method: 'POST' }),
    // Acompanhamento do anúncio: uma entrada por campanha disponível, com os
    // períodos em que valeu e a variação dos valores dela no tempo.
    getItemTracking: (itemId, params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/campaigns/items/${encodeURIComponent(itemId)}/tracking${qs ? `?${qs}` : ''}`);
    },
    // Linha do tempo MENSAL de campanhas: por mês, uma entrada por campanha que
    // valeu no anúncio, com os períodos (runs) recortados ao mês e o
    // líquido/rebate por período. `month` = 'YYYY-MM' (omitido → mês atual).
    getItemMonthlyTimeline: (itemId, params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/campaigns/items/${encodeURIComponent(itemId)}/timeline/month${qs ? `?${qs}` : ''}`);
    },
    getCampaignsByType: () => fetchAPI('/campaigns/by-type'),
    // Consolidado de rebates (D3): totais + por tipo + por campanha das
    // participações vigentes — exibido na aba Relatórios da Inteligência.
    getCampaignRebatesSummary: () => fetchAPI('/campaigns/rebates-summary'),
    // F25: participação automática em campanhas (lista de exclusão do ML).
    getCampaignExclusionStatus: () => fetchAPI('/campaigns/exclusion-status'),
    // Radar de rebate: candidaturas que pagam mais que a campanha ativa.
    getRebateOpportunities: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/campaigns/rebate-opportunities${qs ? `?${qs}` : ''}`);
    },

    // ── Inteligência de Mercado (Fase 4) ─────────────────────────────────────
    // Wire fixo snake_case (briefing, relatórios paginados, oportunidades e
    // calendário). BuildQuery em todos: filtro ausente é AUSÊNCIA de chave,
    // nunca "undefined" serializado (defeito medido no M8).
    getMarketIntelligenceBriefing: () => fetchAPI('/market-intelligence/briefing'),
    getMarketIntelligenceReportsSummary: () => fetchAPI('/market-intelligence/reports/summary'),
    getMarketIntelligenceWithoutCampaign: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/market-intelligence/reports/without-campaign${qs ? `?${qs}` : ''}`);
    },
    getMarketIntelligenceLosingCatalog: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/market-intelligence/reports/losing-catalog${qs ? `?${qs}` : ''}`);
    },
    getMarketIntelligenceSharingCatalog: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/market-intelligence/reports/sharing-catalog${qs ? `?${qs}` : ''}`);
    },
    // `hours` (1..720, clamp no servidor) define a janela das mudanças.
    getMarketIntelligenceCampaignChanges: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/market-intelligence/reports/campaign-changes${qs ? `?${qs}` : ''}`);
    },
    // Candidatos de campanha started em item SEM campanha ativa, ranqueados
    // por units_sold desc dos últimos `days` dias.
    getMarketIntelligenceOpportunities: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/market-intelligence/opportunities${qs ? `?${qs}` : ''}`);
    },
    getMarketIntelligenceCalendar: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/market-intelligence/calendar${qs ? `?${qs}` : ''}`);
    },
    // Elasticidade de preço (semanal): a ORDEM dos itens é do servidor
    // (veredito acionável primeiro, por confiança) — o front não reordena.
    // `search` (opcional) filtra por item_id/SKU/título no SERVIDOR, antes da
    // paginação — total/skipped passam a refletir o recorte buscado. Termo
    // vazio nem viaja: BuildQuery omite a chave (semântica "sem filtro").
    // `group_by` ('item' | 'sku', default item) escolhe a UNIDADE de análise:
    // em 'sku' o motor junta todos os anúncios que dividem o mesmo SKU numa
    // leitura só (cada item volta com `group_key`/`listings_count`, e
    // `skipped.no_sku` conta os anúncios sem SKU que ficaram de fora).
    getMarketIntelligencePriceElasticity: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/market-intelligence/price-elasticity${qs ? `?${qs}` : ''}`);
    },
    // Série semanal completa de UM grupo (inclui semanas de preço carregado e
    // de zero venda) — alimenta o gráfico do detalhe. A chave é o `group_key`
    // da linha (MLB no modo item, SKU no modo sku), e `group_by` precisa
    // acompanhar: é ele que diz ao servidor como interpretar a chave. No modo
    // sku a resposta traz também `listings` (os anúncios do grupo).
    getMarketIntelligencePriceElasticityItem: (groupKey, params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/market-intelligence/price-elasticity/${encodeURIComponent(groupKey)}${qs ? `?${qs}` : ''}`);
    },

    // Previsão de demanda + curva de crescimento (mesma base de vendas da
    // elasticidade). Ordem/filtro/paginação são do servidor; `horizon_weeks`
    // 1..26 (clamp no servidor), a projeção começa na semana corrente.
    getMarketIntelligenceDemandForecast: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/market-intelligence/demand-forecast${qs ? `?${qs}` : ''}`);
    },
    // Série semanal + semanas projetadas de UM grupo (MLB ou SKU, conforme
    // `group_by`), pela mesma chave `group_key` da lista.
    getMarketIntelligenceDemandForecastItem: (groupKey, params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/market-intelligence/demand-forecast/${encodeURIComponent(groupKey)}${qs ? `?${qs}` : ''}`);
    },
    // Curva de crescimento da LOJA (todos os anúncios): semanas, meses e projeção.
    getMarketIntelligenceGrowthCurve: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/market-intelligence/growth-curve${qs ? `?${qs}` : ''}`);
    },

    // ── Precificação (custo fiscal por SKU) ─────────────────────────────────
    // Wire snake_case; BuildQuery omite filtro vazio. Percentuais viajam como
    // FRAÇÃO (0.18 = 18%) — a conversão para % é só da tela.
    getPricingParams: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/pricing/params${qs ? `?${qs}` : ''}`);
    },
    savePricingParams: (data) => fetchAPI('/pricing/params', { method: 'PUT', body: JSON.stringify(data) }),
    getPricingRules: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/pricing/rules${qs ? `?${qs}` : ''}`);
    },
    createPricingRule: (data) => fetchAPI('/pricing/rules', { method: 'POST', body: JSON.stringify(data) }),
    updatePricingRule: (id, data) =>
        fetchAPI(`/pricing/rules/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) }),
    deletePricingRule: (id) => fetchAPI(`/pricing/rules/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    // XML da NF-e de compra vai como texto no JSON (o backend guarda em raw_xml).
    importPricingNfe: (xml) => fetchAPI('/pricing/nfe/import', { method: 'POST', body: JSON.stringify({ xml }) }),
    getPricingNfeList: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/pricing/nfe${qs ? `?${qs}` : ''}`);
    },
    getPricingNfe: (id) => fetchAPI(`/pricing/nfe/${encodeURIComponent(id)}`),
    getPricingLots: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/pricing/lots${qs ? `?${qs}` : ''}`);
    },
    // De-para fornecedor → produto do Bling. O lote aponta para a linha do
    // de-para, então editar aqui é retroativo. `sku: null` desvincula; PUT
    // com sku/fator confirma; `status: 'sugerido'` volta a pendente.
    getPricingSupplierProducts: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/pricing/supplier-products${qs ? `?${qs}` : ''}`);
    },
    updatePricingSupplierProduct: (id, data) =>
        fetchAPI(`/pricing/supplier-products/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) }),
    // Seletor de produto (só products.sku do Bling).
    searchPricingProducts: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/pricing/products${qs ? `?${qs}` : ''}`);
    },
    getPricingSkus: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/pricing/skus${qs ? `?${qs}` : ''}`);
    },
    getPricingSku: (sku) => fetchAPI(`/pricing/skus/${encodeURIComponent(sku)}`),

    // ── Notificações in-app (sino do header) ────────────────────────────────
    // BuildQuery e não URLSearchParams cru: only_unread=false/undefined precisa
    // SUMIR da query (o backend interpreta só o literal 'true').
    getNotificationsFeed: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/notifications/feed${qs ? `?${qs}` : ''}`);
    },
    markNotificationsRead: (ids) =>
        fetchAPI('/notifications/feed/read', { method: 'POST', body: JSON.stringify({ ids }) }),
    markAllNotificationsRead: () => fetchAPI('/notifications/feed/read-all', { method: 'POST' }),

    // Promotions
    getPromotions: () => fetchAPI('/promotions'),
    getItemPromotions: (itemId) => fetchAPI(`/promotions/items/${itemId}`),
    getItemSalePrice: (itemId) => fetchAPI(`/promotions/items/${itemId}/sale-price`),
    // Família: anúncio de lista geral + cópia de catálogo, lado a lado.
    getItemFamily: (itemId) => fetchAPI(`/promotions/items/${itemId}/family`),
    getPromotionDetails: (promotionId, promotionType) => {
        const qs = new URLSearchParams({ promotion_type: promotionType }).toString();
        return fetchAPI(`/promotions/${promotionId}?${qs}`);
    },
    getPromotionItems: (promotionId, promotionType, params = {}) => {
        const qs = new URLSearchParams({ promotion_type: promotionType, ...params }).toString();
        return fetchAPI(`/promotions/${promotionId}/items?${qs}`);
    },
    getItemDetails: (itemId) => fetchAPI(`/promotions/items/${itemId}/details`),
    getItemsBatchDetails: (itemIds) =>
        fetchAPI('/promotions/items/batch-details', {
            method: 'POST',
            body: JSON.stringify({ item_ids: itemIds }),
        }),
    getCompetitors: (itemId) => fetchAPI(`/campaigns/competitors/${itemId}`),

    // Explorador de Endpoints do Mercado Livre (conferência ao vivo)
    getMlExplorerCatalog: () => fetchAPI('/ml-explorer/catalog'),
    runMlExplorerRequest: (endpointId, params = {}) =>
        fetchAPI('/ml-explorer/request', {
            method: 'POST',
            body: JSON.stringify({ endpoint_id: endpointId, params }),
        }),

    // Themes
    getPredefinedThemes: () => fetchAPI('/themes/predefined'),
    getUserThemes: () => fetchAPI('/themes/user'),
    activateUserTheme: (id) => fetchAPI(`/themes/user/${id}/activate`, { method: 'PUT', body: JSON.stringify({}) }),

    // WMS
    getTrucks: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/wms/trucks${qs ? `?${qs}` : ''}`);
    },
    createTruck: (data) => fetchAPI('/wms/trucks', { method: 'POST', body: JSON.stringify(data) }),
    updateTruck: (id, data) => fetchAPI(`/wms/trucks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteTruck: (id) => fetchAPI(`/wms/trucks/${id}`, { method: 'DELETE' }),
    getProducts: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/wms/products${qs ? `?${qs}` : ''}`);
    },
    createProduct: (data) => fetchAPI('/wms/products', { method: 'POST', body: JSON.stringify(data) }),
    getOutbounds: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/wms/outbounds${qs ? `?${qs}` : ''}`);
    },
    // ── RMAs (devoluções e mediações) ────────────────────────────────────────
    getRmas: (params = {}) => {
        const qs = new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')),
        ).toString();
        return fetchAPI(`/rmas${qs ? `?${qs}` : ''}`);
    },
    getRmaMetrics: () => fetchAPI('/rmas/metrics'),
    resolveRmaScan: (code) => fetchAPI(`/rmas/scan/${encodeURIComponent(code)}`),
    getRmaTasks: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/rmas/tasks${qs ? `?${qs}` : ''}`);
    },
    getRma: (id) => fetchAPI(`/rmas/${id}`),
    createRma: (data) => fetchAPI('/rmas', { method: 'POST', body: JSON.stringify(data) }),
    updateRmaStatus: (id, data) => fetchAPI(`/rmas/${id}/status`, { method: 'POST', body: JSON.stringify(data) }),
    addRmaNote: (id, note) => fetchAPI(`/rmas/${id}/notes`, { method: 'POST', body: JSON.stringify({ note }) }),
    uploadRmaAttachments: (id, formData) => fetchAPI(`/rmas/${id}/attachments`, { method: 'POST', body: formData }),
    updateRmaRefund: (id, data) => fetchAPI(`/rmas/${id}/refund`, { method: 'PUT', body: JSON.stringify(data) }),
    getRmaMediationMessages: (id) => fetchAPI(`/rmas/${id}/mediation/messages`),
    registerRmaInspection: (id, formData) => fetchAPI(`/rmas/${id}/inspection`, { method: 'POST', body: formData }),
    // Destino da devolução inspecionada (supervisor, rmas:resolve — ajuste 2 do
    // dono, 2026-09-23): `destination` ESTOQUE | RMA | DESCARTE; só em
    // INSPECIONADO, pode redefinir enquanto o caso não for resolvido.
    setRmaDestination: (id, destination) =>
        fetchAPI(`/rmas/${id}/destination`, { method: 'POST', body: JSON.stringify({ destination }) }),
    markRmaFiscal: (id, type) => fetchAPI(`/rmas/${id}/fiscal`, { method: 'POST', body: JSON.stringify({ type }) }),
    // Marcação fiscal SUBINDO O XML da NF-e (Gestão de Pedidos, rodada 7):
    // multipart com `type` (ENTRADA | PERDA) e UM arquivo `xml` (.xml, ≤ 1 MB).
    markRmaFiscalXml: (id, type, file) => {
        const formData = new FormData();
        formData.append('type', type);
        formData.append('xml', file);
        return fetchAPI(`/rmas/${id}/fiscal`, { method: 'POST', body: formData });
    },
    reconcileRmaRefund: (id) => fetchAPI(`/rmas/${id}/refund/reconcile`, { method: 'POST', body: JSON.stringify({}) }),
    markRmaSupplierRegistered: (id) =>
        fetchAPI(`/rmas/${id}/supplier-registered`, { method: 'POST', body: JSON.stringify({}) }),
    adminUpdateRma: (id, data) => fetchAPI(`/rmas/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    markRmaSupplierShipped: (id, { nf_number, note } = {}) =>
        fetchAPI(`/rmas/${id}/supplier-shipped`, {
            method: 'POST',
            body: JSON.stringify({ nf_number: nf_number || undefined, note: note || undefined }),
        }),
    markRmaSupplierConcluded: (id, outcome, note) =>
        fetchAPI(`/rmas/${id}/supplier-concluded`, {
            method: 'POST',
            body: JSON.stringify({ outcome, note: note || undefined }),
        }),
    // Lotes de RMA ao fornecedor (registro de RMA "em si")
    listSupplierBatches: (status) => fetchAPI(`/rmas/supplier-batches${status ? `?status=${status}` : ''}`),
    getSupplierBatch: (id) => fetchAPI(`/rmas/supplier-batches/${id}`),
    listSupplierBatchCandidates: (q) =>
        fetchAPI(`/rmas/supplier-batches/candidates${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    createSupplierBatch: (data) => fetchAPI('/rmas/supplier-batches', { method: 'POST', body: JSON.stringify(data) }),
    addSupplierBatchItems: (id, rmaIds) =>
        fetchAPI(`/rmas/supplier-batches/${id}/items`, { method: 'POST', body: JSON.stringify({ rma_ids: rmaIds }) }),
    removeSupplierBatchItem: (id, rmaId) =>
        fetchAPI(`/rmas/supplier-batches/${id}/items/${rmaId}`, { method: 'DELETE' }),
    shipSupplierBatch: (id, { nf_number, note } = {}) =>
        fetchAPI(`/rmas/supplier-batches/${id}/ship`, {
            method: 'POST',
            body: JSON.stringify({ nf_number: nf_number || undefined, note: note || undefined }),
        }),
    concludeSupplierBatchItem: (id, rmaId, outcome, note) =>
        fetchAPI(`/rmas/supplier-batches/${id}/conclude-item`, {
            method: 'POST',
            body: JSON.stringify({ rma_id: rmaId, outcome, note: note || undefined }),
        }),
    concludeSupplierBatch: (id, outcome, note) =>
        fetchAPI(`/rmas/supplier-batches/${id}/conclude`, {
            method: 'POST',
            body: JSON.stringify({ outcome, note: note || undefined }),
        }),
    generateRmaLabels: (data) => fetchAPI('/rmas/labels', { method: 'POST', body: JSON.stringify(data) }),
    getRmaLabels: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/rmas/labels${qs ? `?${qs}` : ''}`);
    },
    searchRmaLabelSales: (q) => fetchAPI(`/rmas/labels/sales?q=${encodeURIComponent(q)}`),
    associateRmaLabel: (labelId, externalOrderId) =>
        fetchAPI(`/rmas/labels/${labelId}/associate`, {
            method: 'POST',
            body: JSON.stringify({ external_order_id: externalOrderId }),
        }),

    getWmsHauliers: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/wms/hauliers${qs ? `?${qs}` : ''}`);
    },
    getOutbound: (id) => fetchAPI(`/wms/outbounds/${id}`),
    createOutbound: (formData, { previewOnly = false } = {}) => {
        if (formData instanceof FormData) {
            formData.set('preview_only', previewOnly ? 'true' : 'false');
        }
        return fetchAPI('/wms/outbounds', { method: 'POST', body: formData });
    },
    updateOutboundTransport: (id, data) =>
        fetchAPI(`/wms/outbounds/${id}/transport`, {
            method: 'PUT',
            body: JSON.stringify(data || {}),
        }),
    updateOutboundStatus: (id, status) =>
        fetchAPI(`/wms/outbounds/${id}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status }),
        }),
    uploadOutboundPhotos: (id, formData) => fetchAPI(`/wms/outbounds/${id}/photos`, { method: 'POST', body: formData }),
    uploadOutboundFiles: (id, formData) => fetchAPI(`/wms/outbounds/${id}/files`, { method: 'POST', body: formData }),
    cancelOutbound: (id, formData) => fetchAPI(`/wms/outbounds/${id}/cancel`, { method: 'POST', body: formData }),
    getWmsInboundTasks: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/wms/inbounds/purchase-orders${qs ? `?${qs}` : ''}`);
    },
    receiveWmsInboundTask: (id, formData) =>
        fetchAPI(`/wms/inbounds/purchase-orders/${id}/receive`, { method: 'POST', body: formData }),

    // Product catalog — cadastro PRÓPRIO (2026-09-25). Query/body snake_case e
    // estritos: chave desconhecida (inclusive camelCase) dá 400 no servidor.
    // Lista: search, ean, brand, status, format, origin, sort_by,
    // sort_direction, limit, offset → { results, paging }.
    getCatalogProducts: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/products${qs ? `?${qs}` : ''}`);
    },
    // Detalhe por `products.id` (UUID) — nunca pelo SKU na rota do front.
    getCatalogProduct: (productId) => fetchAPI(`/products/${encodeURIComponent(productId)}`),
    // Lista fechada de marcas (orgs SUPPLIER ativas com assigned_brand).
    getCatalogBrands: () => fetchAPI('/products/brands'),
    // Catálogo de pendências (rótulos + contagens) no escopo da lista.
    getCatalogPending: () => fetchAPI('/products/pending'),
    // SKU novo: regra, conflito (vivo/deletado) e parecidos. stock:write.
    checkCatalogSku: (sku) => fetchAPI(`/products/sku-availability?${BuildQuery({ sku })}`),
    createCatalogProduct: (data) => fetchAPI('/products', { method: 'POST', body: JSON.stringify(data) }),
    // PUT parcial: só as chaves alteradas. `productId` = products.id (UUID).
    updateCatalogProduct: (productId, data) =>
        fetchAPI(`/products/${encodeURIComponent(productId)}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateProductLowStockThreshold: (identifier, threshold) =>
        fetchAPI(`/products/${encodeURIComponent(identifier)}`, {
            method: 'PATCH',
            body: JSON.stringify({ low_stock_threshold: Number(threshold) }),
        }),
    updateProductTags: (identifier, tags) =>
        fetchAPI(`/products/${encodeURIComponent(identifier)}`, {
            method: 'PATCH',
            body: JSON.stringify({ tags: Array.isArray(tags) ? tags : [] }),
        }),

    // Presets da página /operacoes. Multi-user persistido no DB.
    listPresets: () => fetchAPI('/presets'),
    createPreset: (data) =>
        fetchAPI('/presets', {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    updatePreset: (id, patch) =>
        fetchAPI(`/presets/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),
    deletePreset: (id) =>
        fetchAPI(`/presets/${encodeURIComponent(id)}`, {
            method: 'DELETE',
        }),

    // Preferências por usuário (cross-device). NÃO inclui estado de UI momentâneo
    // (resize de colunas, scroll) — só o que faz sentido seguir o user em outro
    // device: layoutMode da /operacoes, último preset selecionado, etc.
    getUserPreferences: () => fetchAPI('/preferences'),
    patchUserPreferences: (patch) =>
        fetchAPI('/preferences', {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),

    // SKUs com outbound ativo (pending/idle/picking/ready). Usado pra computar
    // tag automática 'outbound' no /operacoes sem query N+1 por SKU.
    listSkusWithActiveOutbounds: () => fetchAPI('/wms/outbounds/active-skus'),

    // Colunas customizadas globais (por organização). Presets referenciam por
    // `custom:<id>` no array de columns — editar a expressão aqui propaga pra
    // todos os presets que usam.
    listCustomColumns: () => fetchAPI('/custom-columns'),
    createCustomColumn: (data) =>
        fetchAPI('/custom-columns', {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    updateCustomColumn: (id, patch) =>
        fetchAPI(`/custom-columns/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),
    deleteCustomColumn: (id) =>
        fetchAPI(`/custom-columns/${encodeURIComponent(id)}`, {
            method: 'DELETE',
        }),

    // Organizations
    getOrganizations: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/organizations${qs ? `?${qs}` : ''}`);
    },
    createOrganization: (data) => fetchAPI('/organizations', { method: 'POST', body: JSON.stringify(data) }),
    updateOrganization: (id, data) => fetchAPI(`/organizations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteOrganization: (id) => fetchAPI(`/organizations/${id}`, { method: 'DELETE' }),

    // Purchasing
    getPurchaseOrders: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/purchasing/orders${qs ? `?${qs}` : ''}`);
    },
    getPurchaseOrder: (id) => fetchAPI(`/purchasing/orders/${id}`),
    generatePurchaseOrders: (data) =>
        fetchAPI('/purchasing/orders/generate', { method: 'POST', body: JSON.stringify(data) }),
    updatePurchaseOrderStatus: (id, status, payload = {}) =>
        fetchAPI(`/purchasing/orders/${id}/status`, {
            method: 'PUT',
            body: JSON.stringify({
                status,
                ...(Array.isArray(payload?.items) ? { items: payload.items } : {}),
            }),
        }),
    receivePurchaseOrder: (id, formData) =>
        fetchAPI(`/purchasing/orders/${id}/receive`, { method: 'POST', body: formData }),

    // Payments (tracking ML + MercadoPago)
    getPayments: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/payments${qs ? `?${qs}` : ''}`);
    },
    getPaymentOrderDetail: (orderId) => fetchAPI(`/payments/order/${orderId}`),
    getPayment: (id) => fetchAPI(`/payments/${id}`),
    getOrderMovements: (orderId) => fetchAPI(`/payments/order/${orderId}/movements`),
    importReleaseReport: (fileName) =>
        fetchAPI('/payments/release-report/import', { method: 'POST', body: JSON.stringify({ file_name: fileName }) }),
    getReleaseReportStats: () => fetchAPI('/payments/release-report/stats'),
    // Análise da order (motor de conciliação ML × MP × extrato × NF-e)
    getOrderAnalysis: (orderId) => fetchAPI(`/payments/order/${orderId}/analysis`),
    // Botão "Buscar rebate no ML": consulta agora o desconto de tarifa do pedido
    // no ML e os pagamentos no MP, e devolve o veredito de rebate recalculado.
    refreshOrderRebate: (orderId) =>
        fetchAPI(`/payments/order/${encodeURIComponent(orderId)}/rebate/refresh`, { method: 'POST' }),
    // Experiência de conciliação em 3 vias (Simulado × Previsto × Realizado)
    getOrderExperience: (orderId) => fetchAPI(`/payments/order/${orderId}/experience`),
    getOrderFiscal: (orderId) => fetchAPI(`/payments/order/${orderId}/fiscal`),
    // XML bruto da NF-e (resposta é text/xml, não JSON — fetch direto autenticado)
    getFiscalXml: async (chaveNfe) => {
        const resp = await fetch(`${API_BASE_URL}/payments/fiscal/xml/${chaveNfe}`, {
            headers: { Authorization: `Bearer ${GetToken()}`, 'x-request-id': createRequestId() },
        });
        if (!resp.ok) {
            throw new APIError('XML não encontrado no acervo local', 'XML_NOT_FOUND', resp.status);
        }
        return resp.text();
    },
    listReleaseReports: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/payments/release-report/list${qs ? `?${qs}` : ''}`);
    },

    // Funcionários (entidade de SISTEMA — pessoas ≠ contas de login; o WMS e
    // futuras features referenciam este cadastro). Wire snake_case.
    getEmployees: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/employees${qs ? `?${qs}` : ''}`);
    },
    createEmployee: (data) => fetchAPI('/employees', { method: 'POST', body: JSON.stringify(data) }),
    updateEmployee: (id, patch) =>
        fetchAPI(`/employees/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),

    // Warehouse (módulo WMS novo — M0). Wire snake_case SEM fallback duplo;
    // contrato em MARKDOWN-files/Fable/14-wms/ARQUITETURA-E-ADAPTACOES.md.
    getWarehouseLabels: () => fetchAPI('/warehouse/labels'),
    // Operadores: vínculo funcionário↔função no WMS ({employee_id, role}).
    getWarehouseOperators: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/operators${qs ? `?${qs}` : ''}`);
    },
    createWarehouseOperator: (data) => fetchAPI('/warehouse/operators', { method: 'POST', body: JSON.stringify(data) }),
    updateWarehouseOperator: (id, patch) =>
        fetchAPI(`/warehouse/operators/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),
    // Coletor: identifica pelo ID do funcionário ({code} → operador ativo ou 404).
    identifyWarehouseOperator: (code) =>
        fetchAPI('/warehouse/operators/identify', { method: 'POST', body: JSON.stringify({ code }) }),
    getWarehouseEquipment: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/equipment${qs ? `?${qs}` : ''}`);
    },
    createWarehouseEquipment: (data) =>
        fetchAPI('/warehouse/equipment', { method: 'POST', body: JSON.stringify(data) }),
    updateWarehouseEquipment: (id, patch) =>
        fetchAPI(`/warehouse/equipment/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),
    getWarehouseLpnPrefixes: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/lpn-prefixes${qs ? `?${qs}` : ''}`);
    },
    createWarehouseLpnPrefix: (data) =>
        fetchAPI('/warehouse/lpn-prefixes', { method: 'POST', body: JSON.stringify(data) }),
    updateWarehouseLpnPrefix: (id, patch) =>
        fetchAPI(`/warehouse/lpn-prefixes/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),
    getWarehouseServiceOrderTypes: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/service-order-types${qs ? `?${qs}` : ''}`);
    },
    createWarehouseServiceOrderType: (data) =>
        fetchAPI('/warehouse/service-order-types', { method: 'POST', body: JSON.stringify(data) }),
    updateWarehouseServiceOrderType: (id, patch) =>
        fetchAPI(`/warehouse/service-order-types/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),
    getWarehouseDeposits: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/deposits${qs ? `?${qs}` : ''}`);
    },
    createWarehouseDeposit: (data) => fetchAPI('/warehouse/deposits', { method: 'POST', body: JSON.stringify(data) }),
    updateWarehouseDeposit: (id, patch) =>
        fetchAPI(`/warehouse/deposits/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),
    // M2 — Regras de depósito (evento → depósito destino, §6.3.2). O GET
    // devolve TODAS as event_keys do enum (deposit_id null = nunca
    // configurada); o PUT é upsert por event_key — SEM DELETE (trocar = editar).
    getWarehouseDepositRules: () => fetchAPI('/warehouse/deposit-rules'),
    putWarehouseDepositRule: (eventKey, depositId) =>
        fetchAPI('/warehouse/deposit-rules', {
            method: 'PUT',
            body: JSON.stringify({ event_key: eventKey, deposit_id: depositId }),
        }),
    // M2 — Produtos do WMS: catálogo existente (espelho Bling do melidash) +
    // atributos locais (is_bulky, track_supplier_lot). Paginação SERVIDOR
    // (catálogo grande): search/limit/offset → {results, paging}.
    getWarehouseProducts: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/products${qs ? `?${qs}` : ''}`);
    },
    putWarehouseProductAttributes: (productId, patch) =>
        fetchAPI(`/warehouse/products/${encodeURIComponent(productId)}/attributes`, {
            method: 'PUT',
            body: JSON.stringify(patch),
        }),
    // Re-confirmação de senha do usuário logado (ações significativas — ex.:
    // salvar o Editor de galpão). 200 {ok:true} ou 401.
    confirmPassword: (password) =>
        fetchAPI('/auth/confirm-password', { method: 'POST', body: JSON.stringify({ password }) }),

    // Impressoras de etiqueta (revisão do M1 — etiquetas sempre em ZPL).
    getWarehousePrinters: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/printers${qs ? `?${qs}` : ''}`);
    },
    createWarehousePrinter: (data) => fetchAPI('/warehouse/printers', { method: 'POST', body: JSON.stringify(data) }),
    updateWarehousePrinter: (id, patch) =>
        fetchAPI(`/warehouse/printers/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),
    // Imprimir, testar e detectar impressoras de REDE saem do QZ Tray do
    // navegador (zplPrinting.jsx): o servidor na nuvem não alcança a rede do
    // armazém. As rotas /printers/:id/test|print e /printers/discover do backend
    // continuam para quando o servidor estiver na mesma rede, mas a tela não as usa.

    // Layout espacial do galpão (minimapa configurável — 1 JSON por org).
    getWarehouseMapLayout: () => fetchAPI('/warehouse/map-layout'),
    putWarehouseMapLayout: (layout) =>
        fetchAPI('/warehouse/map-layout', { method: 'PUT', body: JSON.stringify({ layout }) }),

    getWarehouseSettings: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/settings${qs ? `?${qs}` : ''}`);
    },
    // PUT upsert {key, value} — settings não têm DELETE nem active (chave→JSON).
    putWarehouseSetting: (key, value, label) =>
        fetchAPI('/warehouse/settings', {
            method: 'PUT',
            // label só entra quando informado (undefined preserva o existente; ""
            // limpa e volta pro dicionário global/chave — ver putSetting no back).
            body: JSON.stringify({ key, value, ...(label !== undefined ? { label } : {}) }),
        }),
    // Localizações (M1). Cada porta-pallet é um RACK (prefixo de 1–2 letras)
    // com BLOCOS sequenciais — `rack` é STRING, `bay` inteiro. Filtros:
    // rack/bay/type/status/virtual + limit/offset.
    getWarehouseLocations: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/locations${qs ? `?${qs}` : ''}`);
    },
    // Gerador de um rack inteiro: {rack, bays, levels, weights_by_level?,
    // bay_width?, overrides?} → 201 {created, codes_sample}; 409 se o rack já existe.
    generateWarehouseRack: (data) =>
        fetchAPI('/warehouse/locations/generate-rack', { method: 'POST', body: JSON.stringify(data) }),
    // Criação individual com código literal (DCA/STG-DCA/FAB/INC) — 400 se o
    // código não casa o padrão do tipo.
    createWarehouseLocation: (data) => fetchAPI('/warehouse/locations', { method: 'POST', body: JSON.stringify(data) }),
    updateWarehouseLocation: (id, patch) =>
        fetchAPI(`/warehouse/locations/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),
    // Edição em massa por rack/bloco: {rack, bays?, levels?, set: {status?|max_weight_kg?|bay_width?}} → {affected}.
    bulkUpdateWarehouseLocations: (data) =>
        fetchAPI('/warehouse/locations/bulk', { method: 'PATCH', body: JSON.stringify(data) }),
    // Exclusão (setup do M1 — recriar racks/posições criados errado): SÓ se
    // vazia. Rack inteiro (posições + cabeceira STG-A-00 + blocos) ou uma
    // posição. Ocupado (LPN/reserva) => 409 com o que ocupa. Soft delete no
    // back (deleted_at); nunca DELETE físico.
    deleteWarehouseRack: (rack) =>
        fetchAPI(`/warehouse/locations/rack/${encodeURIComponent(rack)}`, { method: 'DELETE' }),
    deleteWarehouseLocation: (id) => fetchAPI(`/warehouse/locations/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    // Editar UM BLOCO (vão) de um rack no editor do mapa: excluir (409 ocupado /
    // 404 se já não existe) ou readicionar/ressuscitar (201; body
    // {levels, weights_by_level?, bay_width?}). Soft delete no back.
    deleteWarehouseRackBay: (rack, bay) =>
        fetchAPI(`/warehouse/locations/rack/${encodeURIComponent(rack)}/bay/${encodeURIComponent(bay)}`, {
            method: 'DELETE',
        }),
    addWarehouseRackBay: (rack, bay, data) =>
        fetchAPI(`/warehouse/locations/rack/${encodeURIComponent(rack)}/bay/${encodeURIComponent(bay)}`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    // Resumo por rack/bloco com contagens por status:
    // {orientation, racks: [{rack, bays: [{bay, total, active, blocked, maintenance}]}]}.
    getWarehouseLocationsMap: () => fetchAPI('/warehouse/locations/map'),
    // Etiquetas de endereço agora são ZPL via GET /warehouse/locations/labels
    // (JSON {zpl, preview}) — o modal usa o client genérico api.get.
    // M10: as etiquetas de bloco de uma vez (?rack= opcional, ?media=).
    // ── MCP — observabilidade (conexões, histórico, resumo, revogar) ────────
    // Painel do admin sobre o uso do MCP (permissão users:read; revogar exige
    // users:write). Wire snake_case, como tudo.
    getMcpGrants: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/mcp/grants${qs ? `?${qs}` : ''}`);
    },
    revokeMcpGrant: (grantId) => fetchAPI(`/mcp/grants/${encodeURIComponent(grantId)}/revoke`, { method: 'POST' }),
    getMcpRequests: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/mcp/requests${qs ? `?${qs}` : ''}`);
    },
    getMcpUsageSummary: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/mcp/summary${qs ? `?${qs}` : ''}`);
    },

    getWarehouseBlockLabelsBatch: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/locations/labels/batch${qs ? `?${qs}` : ''}`);
    },

    // ── M10: rolo de LPNs em branco (inventário inicial) ─────────────────────
    // POST {prefix_code, qty, deposit_id, notes?, media?} → o lote + o ZPL de
    // cada etiqueta. Os LPNs nascem SEM item, numa localização VIRTUAL: valem
    // zero em estoque até alguém bipar.
    createWarehouseLpnBatch: (data) =>
        fetchAPI('/warehouse/lpn-batches', { method: 'POST', body: JSON.stringify(data) }),
    getWarehouseLpnBatches: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/lpn-batches${qs ? `?${qs}` : ''}`);
    },
    getWarehouseLpnBatch: (id) => fetchAPI(`/warehouse/lpn-batches/${encodeURIComponent(id)}`),
    // Reimpressão do rolo; only_unused=true traz só o que ainda não virou pallet.
    getWarehouseLpnBatchLabels: (id, params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/lpn-batches/${encodeURIComponent(id)}/labels${qs ? `?${qs}` : ''}`);
    },

    // ── M10: bipe de PRODUTO ─────────────────────────────────────────────────
    // POST {code} → {status: RESOLVED|AMBIGUOUS|NOT_FOUND, product, candidates}.
    // AMBIGUOUS é o caso do EAN cadastrado em dois produtos (unidade × caixa):
    // a tela PERGUNTA, o servidor não escolhe.
    scanWarehouseProduct: (code) =>
        fetchAPI('/warehouse/products/scan', { method: 'POST', body: JSON.stringify({ code }) }),

    // ── M10: inventário inicial ──────────────────────────────────────────────
    // POST {lpn_code, product_code, qty, location_code, ...} → o LPN preenchido.
    intakeWarehouseLpn: (data) => fetchAPI('/warehouse/lpns/intake', { method: 'POST', body: JSON.stringify(data) }),

    // ── M10: integração com o checkout (permissão `wms:integration`) ─────────
    getWarehouseIntegrationStock: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/integration/stock${qs ? `?${qs}` : ''}`);
    },
    createWarehouseReservation: (data) =>
        fetchAPI('/warehouse/integration/reservations', { method: 'POST', body: JSON.stringify(data) }),
    getWarehouseReservation: (sourceRef, params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/integration/reservations/${encodeURIComponent(sourceRef)}${qs ? `?${qs}` : ''}`);
    },
    consumeWarehouseReservation: (sourceRef, data) =>
        fetchAPI(`/warehouse/integration/reservations/${encodeURIComponent(sourceRef)}/consume`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    releaseWarehouseReservation: (sourceRef, data = {}) =>
        fetchAPI(`/warehouse/integration/reservations/${encodeURIComponent(sourceRef)}/release`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    // LPNs (M3 — recebimento). Permissão `wms:manage:lpns`. Filtros do wire:
    // code (busca por código), status, location_id, product_id + limit/offset.
    // A CONSULTA POR CÓDIGO é a chave natural do LPN ("onde está"): o GET
    // unitário usa o CÓDIGO na URL, não o id — é o que vem do QR/etiqueta.
    // ── INBOUND contra ORDEM DE COMPRA (console de recebimento) ────────────
    getWarehouseInboundPurchaseOrders: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/inbound/purchase-orders${qs ? `?${qs}` : ''}`);
    },
    getWarehouseInboundPurchaseOrder: (id) => fetchAPI(`/warehouse/inbound/purchase-orders/${encodeURIComponent(id)}`),
    // POST {purchase_order_id, prefix_code, location_id, deposit_id,
    // invoice_id?, is_bulky?, items:[{purchase_order_item_id?|product_id?,
    // qty, supplier_lot?, expiry_date?}]} → {lpn (com zpl), warnings,
    // receipts_created, order_progress}. UM pallet por chamada; parcial é o
    // caso normal; divergência vira warning, nunca bloqueio.
    createWarehouseInboundReceipt: (data) =>
        fetchAPI('/warehouse/inbound/receipts', { method: 'POST', body: JSON.stringify(data) }),
    // Consulta a chave (44) nos acervos locais + no cadastro (não grava nada).
    lookupWarehouseInboundInvoice: (accessKey) =>
        fetchAPI(`/warehouse/inbound/invoice-lookup?access_key=${encodeURIComponent(accessKey)}`),
    registerWarehouseInboundInvoice: (data) =>
        fetchAPI('/warehouse/inbound/invoices', { method: 'POST', body: JSON.stringify(data) }),

    // ── ENTRADA DE DEVOLUÇÕES (WMS — independente do módulo rmas) ──────────
    getWarehouseReturns: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/returns${qs ? `?${qs}` : ''}`);
    },
    getWarehouseReturn: (id) => fetchAPI(`/warehouse/returns/${encodeURIComponent(id)}`),
    createWarehouseReturn: (data) => fetchAPI('/warehouse/returns', { method: 'POST', body: JSON.stringify(data) }),
    updateWarehouseReturn: (id, patch) =>
        fetchAPI(`/warehouse/returns/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    // POST {disposition: SELLABLE|SUPPLIER_RMA, location_id, deposit_id?,
    // prefix_code?, items:[{product_id, qty, supplier_lot?, expiry_date?,
    // notes?}]} → {lpn, warnings, items_created, deposit_code, location_code}.
    triageWarehouseReturn: (id, data) =>
        fetchAPI(`/warehouse/returns/${encodeURIComponent(id)}/triage`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    // ── Navios (tracker das importações) ─────────────────────────────────
    getNavios: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/navios${qs ? `?${qs}` : ''}`);
    },
    getNavio: (id) => fetchAPI(`/navios/${encodeURIComponent(id)}`),
    createNavio: (data) => fetchAPI('/navios', { method: 'POST', body: JSON.stringify(data) }),
    updateNavio: (id, data) =>
        fetchAPI(`/navios/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(data) }),
    addNavioPosicao: (id, data) =>
        fetchAPI(`/navios/${encodeURIComponent(id)}/posicoes`, { method: 'POST', body: JSON.stringify(data) }),
    syncNavios: () => fetchAPI('/navios/sync', { method: 'POST' }),

    // ── Coletas (gestão de envios ML) ────────────────────────────────────
    getColetasResumo: () => fetchAPI('/coletas/resumo'),
    getColetasElegiveis: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/coletas/elegiveis${qs ? `?${qs}` : ''}`);
    },
    getColetasDemandas: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/coletas/demandas${qs ? `?${qs}` : ''}`);
    },
    getColetasDiagnostico: (shipmentId) => fetchAPI(`/coletas/diagnostico/${encodeURIComponent(shipmentId)}`),
    getColetasLotes: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/coletas/lotes${qs ? `?${qs}` : ''}`);
    },
    getColetasLoteItens: (id) => fetchAPI(`/coletas/lotes/${encodeURIComponent(id)}/itens`),
    createColetaLote: (data) => fetchAPI('/coletas/lotes', { method: 'POST', body: JSON.stringify(data) }),
    // Cockpit de Coletas — consolida /cockpit/avancado + picking + monitor do
    // zpl-checkout lendo o Postgres dele (somente leitura, org ROOT).
    getColetasDashboard: () => fetchAPI('/coletas/dashboard'),
    getColetasPackingEstado: (operatorCode) =>
        fetchAPI(`/coletas/packing/estado?operator_code=${encodeURIComponent(operatorCode)}`),
    postColetasPackingBipar: (data) =>
        fetchAPI('/coletas/packing/bipar', { method: 'POST', body: JSON.stringify(data) }),
    postColetasPackingEmitir: (data) =>
        fetchAPI('/coletas/packing/emitir', { method: 'POST', body: JSON.stringify(data) }),
    postColetasPackingBiparEtiqueta: (data) =>
        fetchAPI('/coletas/packing/bipar-etiqueta', { method: 'POST', body: JSON.stringify(data) }),
    getColetasEnviosDias: () => fetchAPI('/coletas/envios/dias'),
    getColetasEnviosDiaProdutos: (dia) => fetchAPI(`/coletas/envios/dias/${encodeURIComponent(dia)}/produtos`),
    getColetasEnviosDoDia: (dia, params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/coletas/envios/dias/${encodeURIComponent(dia)}${qs ? `?${qs}` : ''}`);
    },
    getColetasCarrinhoDemandas: () => fetchAPI('/coletas/carrinho/demandas'),
    // O lote em ZPL (identificação + etiquetas). `a_partir_de` reimprime do
    // ponto onde a impressora parou, em vez do lote inteiro.
    reabrirColetaLote: (id) => fetchAPI(`/coletas/lotes/${encodeURIComponent(id)}/reabrir`, { method: 'POST' }),
    getColetaLoteZpl: (id, params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/coletas/lotes/${encodeURIComponent(id)}/zpl${qs ? `?${qs}` : ''}`);
    },
    // "A última que saiu certa foi a Nª": até N vira impressa, o resto volta.
    postColetaLoteImpressoAte: (id, ordem) =>
        fetchAPI(`/coletas/lotes/${encodeURIComponent(id)}/impresso-ate`, {
            method: 'POST',
            body: JSON.stringify({ ordem }),
        }),
    // A mesma coisa pelo BIPE do envio da última etiqueta certa (sem contar).
    postColetaLoteUltimaCorreta: (id, codigo) =>
        fetchAPI(`/coletas/lotes/${encodeURIComponent(id)}/ultima-correta`, {
            method: 'POST',
            body: JSON.stringify({ codigo }),
        }),
    // Os materiais do lote (SKU, EAN, unidades) — o 1º passo da impressão.
    getColetaLoteMateriais: (id) => fetchAPI(`/coletas/lotes/${encodeURIComponent(id)}/materiais`),
    // A tela mandou o lote para a impressora, ou baixou o .zpl (a lista passa a
    // oferecer "Recuperar"). `ordens` = os itens que entraram na pilha (o
    // `ordens` do GET /zpl) — só eles podem virar impressos ao finalizar.
    postColetaLoteImpressao: (id, { aPartirDe = 1, ordens }) =>
        fetchAPI(`/coletas/lotes/${encodeURIComponent(id)}/impressao`, {
            method: 'POST',
            body: JSON.stringify({ a_partir_de: aPartirDe, ordens }),
        }),
    // Finalizar: o lote encerra e os envios cuja etiqueta foi para a impressora
    // ficam impressos (o resto volta para a fila).
    finalizarColetaLote: (id) => fetchAPI(`/coletas/lotes/${encodeURIComponent(id)}/finalizar`, { method: 'POST' }),
    postColetasCarrinhoLote: (data) =>
        fetchAPI('/coletas/carrinho/lote', { method: 'POST', body: JSON.stringify(data) }),
    postColetasCarrinhoOs: (data) => fetchAPI('/coletas/carrinho/os', { method: 'POST', body: JSON.stringify(data) }),
    // QZ TRAY — certificado (texto puro) e assinatura. Sem este par o QZ trata
    // o site como desconhecido e pede "Allow" a cada etiqueta.
    getQzCertificate: async () => {
        const resp = await fetch(`${API_BASE_URL}/warehouse/qz/certificate`, {
            headers: { Authorization: `Bearer ${GetToken()}`, 'x-request-id': createRequestId() },
        });
        if (!resp.ok) {
            return '';
        }
        return resp.text();
    },
    postQzSign: (desafio) => fetchAPI('/warehouse/qz/sign', { method: 'POST', body: JSON.stringify({ desafio }) }),

    // ZPL é texto puro (vai direto pra impressora), não JSON — fetch direto autenticado.
    getColetasZpl: async (shipmentId) => {
        const resp = await fetch(`${API_BASE_URL}/coletas/envios/${encodeURIComponent(shipmentId)}/zpl`, {
            headers: { Authorization: `Bearer ${GetToken()}`, 'x-request-id': createRequestId() },
        });
        if (!resp.ok) {
            let mensagem = 'Etiqueta não disponível';
            try {
                mensagem = (await resp.json())?.error?.message || mensagem;
            } catch {
                // resposta não-JSON: mantém a mensagem padrão
            }
            throw new APIError(mensagem, 'ZPL_NOT_FOUND', resp.status);
        }
        return resp.text();
    },

    getWarehouseLpns: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/lpns${qs ? `?${qs}` : ''}`);
    },
    getWarehouseLpn: (code) => fetchAPI(`/warehouse/lpns/${encodeURIComponent(code)}`),
    // POST {prefix_code, location_id, deposit_id, is_bulky?, origin_type?,
    // origin_id?, items:[{product_id, qty, supplier_lot?, expiry_date?}]} → LPN
    // criado (o backend gera o código do dia e o `zpl` da etiqueta).
    createWarehouseLpn: (data) => fetchAPI('/warehouse/lpns', { method: 'POST', body: JSON.stringify(data) }),
    // PATCH por ID {status?, location_id?, is_bulky?} — SEM DELETE (fechar é
    // status CLOSED).
    updateWarehouseLpn: (id, patch) =>
        fetchAPI(`/warehouse/lpns/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),

    // ── M4 — Coletor: classificador, movimentos e incidentes ────────────────
    // O CLASSIFICADOR é o único lugar do sistema que faz parsing de código
    // (PRD §2.2): manda `{code}` cru e o backend responde o que aquilo é —
    // `{type: LOCATION|BLOCK|LPN|UNKNOWN}` com o registro correspondente (LPN
    // traz itens + onde está; posição traz os LPNs dela; bloco traz as
    // posições e a ocupação). O front NUNCA decide o tipo por regex.
    scanWarehouseCode: (code) => fetchAPI('/warehouse/scan', { method: 'POST', body: JSON.stringify({ code }) }),
    // Movimento de LPN. `scan_method` (SCANNED|TYPED) é obrigatório — é o que
    // distingue na trilha o que foi lido do que foi digitado. Bloqueio da
    // engine de movimentação vem como **409** com `error.details.blocks`
    // (`[{rule, message}]`) — a tela vermelha do coletor lê essa lista.
    createWarehouseMovement: (data) => fetchAPI('/warehouse/movements', { method: 'POST', body: JSON.stringify(data) }),
    // Incidentes. Abrir é `wms:operate` (o coletor abre do chão); listar e
    // resolver é `wms:manage:incidents` (painel do Supervisor). SEM DELETE —
    // resolver é PATCH de status.
    getWarehouseIncidents: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/incidents${qs ? `?${qs}` : ''}`);
    },
    createWarehouseIncident: (data) => fetchAPI('/warehouse/incidents', { method: 'POST', body: JSON.stringify(data) }),
    // Ficha COMPLETA de UM incidente: abertura (quem/quando/o que foi
    // relatado), posição e LPN com os códigos, resolução e `notes_history` —
    // a linha do tempo das anotações (autor + quando + texto), em ordem.
    getWarehouseIncident: (id) => fetchAPI(`/warehouse/incidents/${encodeURIComponent(id)}`),
    // Anotar o incidente. O histórico é APPEND-ONLY: só existe POST — não há
    // editar nem apagar nota, aqui nem no backend. Incidente já RESOLVIDO
    // recusa nota nova com 409 `WMS26_INCIDENT_RESOLVED` (reabra e anote).
    // `operatorCode` = crachá de quem escreveu, quando a nota vem do coletor
    // (conta compartilhada de dispositivo). Do console fica indefinido: ali a
    // conta logada JÁ é a pessoa, e o backend guarda `created_by`.
    createWarehouseIncidentNote: (id, body, operatorCode = null) =>
        fetchAPI(`/warehouse/incidents/${encodeURIComponent(id)}/notes`, {
            method: 'POST',
            body: JSON.stringify({ body, operator_code: operatorCode || undefined }),
        }),
    // PATCH {status?, notes?}. ⚠️ `notes` NÃO sobrescreve o texto de abertura
    // do incidente: vira a NOTA FINAL do histórico, assinada por quem resolveu.
    updateWarehouseIncident: (id, patch) =>
        fetchAPI(`/warehouse/incidents/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),

    // Ordens de Serviço (M5). O console (`wms:manage:service_orders`) enxerga a
    // fila inteira; o coletor (`wms:operate`) só as READY e as suas.
    //
    // ⚠️ Toda ação do CHÃO manda `operator_code` — a conta do coletor é
    // compartilhada (dispositivo), então quem age vem do crachá, nunca do
    // usuário logado. Ações do console não mandam: ali a conta já é a pessoa.
    getWarehouseServiceOrders: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/service-orders${qs ? `?${qs}` : ''}`);
    },
    getWarehouseServiceOrder: (id) => fetchAPI(`/warehouse/service-orders/${encodeURIComponent(id)}`),
    createWarehouseServiceOrder: (data) =>
        fetchAPI('/warehouse/service-orders', { method: 'POST', body: JSON.stringify(data) }),
    updateWarehouseServiceOrder: (id, patch) =>
        fetchAPI(`/warehouse/service-orders/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),
    // Aceite FIRST-WINS: quem chegar depois recebe 409 — a corrida é resolvida
    // no banco, então o 409 aqui é resposta legítima, não falha de rede.
    // `equipmentCode` = o equipamento da SESSÃO do coletor. Quando vem, VENCE o
    // que o Supervisor planejou na OS: planejamento é intenção, execução é
    // fato — e quem move o pallet é a máquina que está na mão do operador (é
    // ela que faz `last_position` andar, §6.4).
    acceptWarehouseServiceOrder: (id, operatorCode, equipmentCode = null) =>
        fetchAPI(`/warehouse/service-orders/${encodeURIComponent(id)}/accept`, {
            method: 'POST',
            body: JSON.stringify({
                operator_code: operatorCode,
                equipment_code: equipmentCode || undefined,
            }),
        }),
    // Pausar CARREGANDO algo obriga dizer onde o pallet ficou (§6.5) — o
    // sistema não pode perder a carga de vista. `carryingLpnCode` é o LPN na
    // mão: o item não fica marcado como "em execução" (não existe endpoint de
    // iniciar item), então quem sabe o que está sendo carregado é o coletor,
    // não o banco. `scanMethod` acompanha porque o drop grava um MOVE real.
    pauseWarehouseServiceOrder: (
        id,
        { reason, operatorCode, dropLocationCode = null, carryingLpnCode = null, scanMethod = null },
    ) =>
        fetchAPI(`/warehouse/service-orders/${encodeURIComponent(id)}/pause`, {
            method: 'POST',
            body: JSON.stringify({
                reason,
                operator_code: operatorCode,
                drop_location_code: dropLocationCode || undefined,
                carrying_lpn_code: carryingLpnCode || undefined,
                scan_method: scanMethod || undefined,
            }),
        }),
    resumeWarehouseServiceOrder: (id, operatorCode) =>
        fetchAPI(`/warehouse/service-orders/${encodeURIComponent(id)}/resume`, {
            method: 'POST',
            body: JSON.stringify({ operator_code: operatorCode }),
        }),
    executeWarehouseServiceOrderItem: (id, itemId, payload) =>
        fetchAPI(`/warehouse/service-orders/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}/execute`, {
            method: 'POST',
            body: JSON.stringify(payload),
        }),
    // ⚠️ Endpoint SEPARADO do execute, não uma flag: "executar com sinalizador
    // de não executei" é a assinatura que faz alguém pular item sem querer.
    // Pular é decisão consciente — abre incidente e a OS SEGUE (item com
    // problema não trava a rota, §6.5).
    skipWarehouseServiceOrderItem: (id, itemId, payload) =>
        fetchAPI(`/warehouse/service-orders/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}/skip`, {
            method: 'POST',
            body: JSON.stringify(payload),
        }),
    // Preview NÃO grava: é o Supervisor vendo quais OSs sairiam (aéreo →
    // LIFT+TRANSPORT; térreo → só TRANSPORT) e podendo fatiar antes.
    previewWarehouseAllocation: (data) =>
        fetchAPI('/warehouse/service-orders/allocation/preview', { method: 'POST', body: JSON.stringify(data) }),
    createWarehouseAllocation: (data) =>
        fetchAPI('/warehouse/service-orders/allocation', { method: 'POST', body: JSON.stringify(data) }),
    // Comparador dos 6 algoritmos: devolve um resultado por algoritmo com o
    // breakdown (deslocamento/vertical/manuseio) e a geometria para desenhar.
    compareWarehouseRoutes: (data) =>
        fetchAPI('/warehouse/routes/compare', { method: 'POST', body: JSON.stringify(data) }),

    // REPACK e picking fracionado (M7). A PRÉVIA é o mesmo motor da execução:
    // a tela nunca soma saldo no navegador — quem conta é o servidor, nos dois
    // caminhos, senão o que a tela promete pode não ser o que o banco grava.
    previewRepack: (data) => fetchAPI('/warehouse/repack/preview', { method: 'POST', body: JSON.stringify(data) }),
    // SPLIT (1→N): o LPN de origem NÃO morre por ser desmembrado (§6.2) — ele
    // morre quando o conteúdo zera.
    splitLpn: (data) => fetchAPI('/warehouse/repack/split', { method: 'POST', body: JSON.stringify(data) }),
    /** MERGE / transferência (N→1). N = 1 é a transferência. */
    mergeLpns: (data) => fetchAPI('/warehouse/repack/merge', { method: 'POST', body: JSON.stringify(data) }),
    // ⚠️ PICKING FRACIONADO **não gera LPN**: credita em consumo de OP ou em
    // volume de expedição. Sem um desses dois destinos o backend RECUSA (409) —
    // é o invariante de saldo do §3 se defendendo: sem destino, a mercadoria
    // sairia do sistema.
    pickFractional: (data) => fetchAPI('/warehouse/repack/pick', { method: 'POST', body: JSON.stringify(data) }),
    // Seletor em ordem FIFO (§6.2): `fifo_rank` 1 = o mais antigo. A ordem é a
    // informação — o FIFO AVISA e nunca bloqueia.
    getRepackCandidates: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/repack/candidates${qs ? `?${qs}` : ''}`);
    },
    // Volumes de expedição — o TERCEIRO lugar onde o saldo pode viver (§3).
    getShipmentVolumes: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/shipment-volumes${qs ? `?${qs}` : ''}`);
    },
    getShipmentVolume: (id) => fetchAPI(`/warehouse/shipment-volumes/${encodeURIComponent(id)}`),

    // EXPEDIÇÃO (M8). O embarque é a sessão de CARREGAMENTO de um romaneio que
    // já existe: o romaneio sabe o que foi vendido, o embarque sabe o que
    // entrou no veículo.
    getWarehouseShipments: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/shipments${qs ? `?${qs}` : ''}`);
    },
    getWarehouseShipment: (id) => fetchAPI(`/warehouse/shipments/${encodeURIComponent(id)}`),
    createWarehouseShipment: (data) => fetchAPI('/warehouse/shipments', { method: 'POST', body: JSON.stringify(data) }),
    addWarehouseShipmentItems: (id, data) =>
        fetchAPI(`/warehouse/shipments/${encodeURIComponent(id)}/items`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    // "Excluir" item do checklist é PATCH {active:false} — sem DELETE, como todo
    // o resto do módulo.
    removeWarehouseShipmentItem: (id, itemId) =>
        fetchAPI(`/warehouse/shipments/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}`, {
            method: 'PATCH',
            body: JSON.stringify({ active: false }),
        }),
    // Bipe do carregamento: SÓ a etiqueta do LPN/volume. Estar na doca deixou de
    // ser uma segunda leitura (13/08) e voltou a ser o que sempre foi no resto
    // do sistema — a POSIÇÃO do pallet, registrada no instante do bipe.
    scanWarehouseShipmentItem: (id, data) =>
        fetchAPI(`/warehouse/shipments/${encodeURIComponent(id)}/scan`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    // ⚠️ FECHAR vai com **FormData** (a mídia do carregamento viaja junto), então
    // NÃO serializa JSON: o `fetchAPI` omite o Content-Type quando o corpo é
    // FormData para o navegador escrever o boundary. E a trava de 100% é REFEITA
    // aqui no servidor — 409 depois de a tela mostrar 100% significa que o
    // checklist mudou, não que a tela errou.
    closeWarehouseShipment: (id, formData) =>
        fetchAPI(`/warehouse/shipments/${encodeURIComponent(id)}/close`, { method: 'POST', body: formData }),
    // ⚠️ Cancelar é PATCH de STATUS, não uma rota `/cancel` — eu tinha escrito
    // `POST .../cancel`, que responde "Route not found". O fim de vida do
    // embarque é uma transição de estado, como no resto do módulo.
    cancelWarehouseShipment: (id, data = {}) =>
        fetchAPI(`/warehouse/shipments/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify({ status: 'CANCELED', ...data }),
        }),
    // A fila do espelho: é ela que responde "por que o Bling não atualizou?".
    getBlingSyncQueue: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/bling-sync-queue${qs ? `?${qs}` : ''}`);
    },
    requeueBlingSyncQueue: (data = {}) =>
        fetchAPI('/warehouse/bling-sync-queue/requeue', { method: 'POST', body: JSON.stringify(data) }),

    // INVENTÁRIO (M9). ⚠️ Todo número destes painéis é DERIVADO da trilha de
    // movimentos ou do saldo — nunca de contador mantido à parte. É por isso que
    // a CONCILIAÇÃO tem endpoint próprio: ela é a prova de que os outros três
    // podem ser acreditados.
    getInventoryOccupancy: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/inventory/occupancy${qs ? `?${qs}` : ''}`);
    },
    getInventoryAging: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/inventory/aging${qs ? `?${qs}` : ''}`);
    },
    getInventorySla: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/inventory/sla${qs ? `?${qs}` : ''}`);
    },
    getInventoryReconciliation: (params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/warehouse/inventory/reconciliation${qs ? `?${qs}` : ''}`);
    },
    // A contagem mostra o ESPERADO antes de o operador confirmar (contagem não é
    // cega hoje — decisão em aberto no TESTE-M9).
    getCountExpectation: (id, itemId) =>
        fetchAPI(`/warehouse/service-orders/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}/count`),
    // Divergência abre incidente e a contagem SEGUE — item divergente não trava
    // a rota, mesma regra do item pulado do M5.
    countServiceOrderItem: (id, itemId, payload) =>
        fetchAPI(`/warehouse/service-orders/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}/count`, {
            method: 'POST',
            body: JSON.stringify(payload),
        }),
    // Atendimento (perguntas pré-venda + mensagens pós-venda do ML)
    getAtendimentoSummary: () => fetchAPI('/atendimento/summary'),
    getAtendimentoQuestions: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/atendimento/questions${qs ? `?${qs}` : ''}`);
    },
    getAtendimentoQuestion: (id) => fetchAPI(`/atendimento/questions/${id}`),
    answerAtendimentoQuestion: (id, text) =>
        fetchAPI(`/atendimento/questions/${id}/answer`, { method: 'POST', body: JSON.stringify({ text }) }),
    suggestAtendimentoQuestion: (id) => fetchAPI(`/atendimento/questions/${id}/suggest`, { method: 'POST' }),
    getAtendimentoConversations: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/atendimento/conversations${qs ? `?${qs}` : ''}`);
    },
    getAtendimentoConversation: (id) => fetchAPI(`/atendimento/conversations/${id}`),
    sendAtendimentoMessage: (id, text) =>
        fetchAPI(`/atendimento/conversations/${id}/messages`, { method: 'POST', body: JSON.stringify({ text }) }),
    suggestAtendimentoConversation: (id) => fetchAPI(`/atendimento/conversations/${id}/suggest`, { method: 'POST' }),
    openAtendimentoConversation: (reference) =>
        fetchAPI('/atendimento/conversations/open', { method: 'POST', body: JSON.stringify({ reference }) }),
    syncAtendimento: () => fetchAPI('/atendimento/sync', { method: 'POST' }),
    // Respostas rápidas (mensagens pré-definidas)
    getAtendimentoQuickReplies: () => fetchAPI('/atendimento/quick-replies'),
    createAtendimentoQuickReply: (payload) =>
        fetchAPI('/atendimento/quick-replies', { method: 'POST', body: JSON.stringify(payload) }),
    updateAtendimentoQuickReply: (id, payload) =>
        fetchAPI(`/atendimento/quick-replies/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
    deleteAtendimentoQuickReply: (id) => fetchAPI(`/atendimento/quick-replies/${id}`, { method: 'DELETE' }),
    // Documento de produto (cadastro por SKU)
    getAtendimentoProducts: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/atendimento/products${qs ? `?${qs}` : ''}`);
    },
    getAtendimentoProduct: (sku) => fetchAPI(`/atendimento/products/${encodeURIComponent(sku)}`),
    saveAtendimentoProduct: (sku, payload) =>
        fetchAPI(`/atendimento/products/${encodeURIComponent(sku)}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        }),
    deleteAtendimentoProduct: (sku) =>
        fetchAPI(`/atendimento/products/${encodeURIComponent(sku)}`, { method: 'DELETE' }),
    // Base de conhecimento (fichas de produto + políticas gerais)
    getAtendimentoContext: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/atendimento/context${qs ? `?${qs}` : ''}`);
    },
    getAtendimentoKnowledge: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return fetchAPI(`/atendimento/knowledge${qs ? `?${qs}` : ''}`);
    },
    createAtendimentoKnowledge: (payload) =>
        fetchAPI('/atendimento/knowledge', { method: 'POST', body: JSON.stringify(payload) }),
    updateAtendimentoKnowledge: (id, payload) =>
        fetchAPI(`/atendimento/knowledge/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
    deleteAtendimentoKnowledge: (id) => fetchAPI(`/atendimento/knowledge/${id}`, { method: 'DELETE' }),

    // ── Gestão de Pedidos (/pedidos): tags automáticas, tags manuais e histórico
    // Wire snake_case (§2.3 da spec). `tags`/`manual_tags` aceitam array ou csv.
    listPedidos: (params = {}) => {
        const qs = BuildQuery(joinCsvParams(params, ['tags', 'manual_tags']));
        return fetchAPI(`/pedidos${qs ? `?${qs}` : ''}`);
    },
    getPedidosSummary: (params = {}) => {
        const qs = BuildQuery(joinCsvParams(params, ['manual_tags']));
        return fetchAPI(`/pedidos/summary${qs ? `?${qs}` : ''}`);
    },
    getPedido: (id) => fetchAPI(`/pedidos/${encodeURIComponent(id)}`),
    searchPedidoBlingOrder: (id, body = {}) =>
        fetchAPI(`/pedidos/${encodeURIComponent(id)}/bling/buscar`, { method: 'POST', body: JSON.stringify(body) }),
    linkPedidoBlingOrder: (id, body) =>
        fetchAPI(`/pedidos/${encodeURIComponent(id)}/bling`, { method: 'PUT', body: JSON.stringify(body) }),
    unlinkPedidoBlingOrder: (id) => fetchAPI(`/pedidos/${encodeURIComponent(id)}/bling`, { method: 'DELETE' }),
    // Linha do tempo mesclada (rodada 3): `{ limit, before (ISO), source? }`;
    // responde `{ events, next_before, sources }`.
    getPedidoHistory: (id, params = {}) => {
        const qs = BuildQuery(params);
        return fetchAPI(`/pedidos/${encodeURIComponent(id)}/history${qs ? `?${qs}` : ''}`);
    },
    // "Sincronizar" do detalhe (rodada 3): refaz venda/envio/conversa no ML —
    // `{ synced_at, sources: { venda|envio|conversa: { ok, at, error } } }`.
    syncPedido: (id) => fetchAPI(`/pedidos/${encodeURIComponent(id)}/sync`, { method: 'POST' }),
    // "Sincronizar tudo" da lista (orders:write): dispara os 6 jobs de sync em
    // sequência no background e responde na hora — `{ started, skipped_running,
    // cooldown_until }`; 409 com `retry_after` (s) dentro do cooldown de 2 min.
    syncAll: () => fetchAPI('/pedidos/sync-all', { method: 'POST' }),
    // `{ running, jobs: [{ name, label, status, last_run, last_duration_ms }],
    // last_synced_at }` (orders:read).
    syncAllStatus: () => fetchAPI('/pedidos/sync-all/status'),
    addPedidoNote: (id, note) =>
        fetchAPI(`/pedidos/${encodeURIComponent(id)}/notes`, { method: 'POST', body: JSON.stringify({ note }) }),
    listPedidoTagDefinitions: () => fetchAPI('/pedidos/tags'),
    createPedidoTagDefinition: (body) => fetchAPI('/pedidos/tags', { method: 'POST', body: JSON.stringify(body) }),
    updatePedidoTagDefinition: (id, body) =>
        fetchAPI(`/pedidos/tags/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
    deletePedidoTagDefinition: (id) => fetchAPI(`/pedidos/tags/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    assignPedidoTag: (id, tagId) =>
        fetchAPI(`/pedidos/${encodeURIComponent(id)}/tags`, {
            method: 'POST',
            body: JSON.stringify({ tag_id: tagId }),
        }),
    removePedidoTag: (id, tagId) =>
        fetchAPI(`/pedidos/${encodeURIComponent(id)}/tags/${encodeURIComponent(tagId)}`, { method: 'DELETE' }),
    // Conciliação financeira do pedido (aba Financeiro): marca e desmarca.
    setPedidoConciliacao: (id, { reconciled, note = null }) =>
        fetchAPI(`/pedidos/${encodeURIComponent(id)}/conciliacao`, {
            method: 'POST',
            body: JSON.stringify({ reconciled, note }),
        }),
    // Aba JSON raw (só `developer`): o JSON como está no banco, por fonte.
    getPedidoPayloads: (id) => fetchAPI(`/pedidos/${encodeURIComponent(id)}/payloads`),
    // Botão "Request": refaz a consulta no endpoint daquele bloco, pelo backend.
    requestPedidoPayload: (id, { source, item_id = null }) =>
        fetchAPI(`/pedidos/${encodeURIComponent(id)}/payloads/request`, {
            method: 'POST',
            body: JSON.stringify({ source, item_id }),
        }),
    // Regras (rodada 3b): `{ rules: [{ key, title, description, value, default,
    // min, max, nullable, updated_by_name, updated_at }] }`; PUT `{ value }`
    // (orders:write; `null` = sem prazo, só se `nullable`).
    getPedidoRegras: () => fetchAPI('/pedidos/regras'),
    updatePedidoRegra: (key, value) =>
        fetchAPI(`/pedidos/regras/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify({ value }) }),

    // ── Gestão de Pedidos — rodada 5 (2026-09-29) ───────────────────────────
    // Histórico do comprador (mesmo buyer_id, no escopo): `{ buyer, summary,
    // alert, orders, truncated }` (orders:read).
    getPedidoComprador: (id) => fetchAPI(`/pedidos/${encodeURIComponent(id)}/comprador`),
    // Status do financeiro do pedido (rmas:finance:update): `status`
    // NAO_VISTO | VISTO | PENDENCIA (PENDENCIA exige nota); nota vazia não
    // viaja. Responde `{ finance_review }`.
    setPedidoFinanceStatus: (id, { status, note = null }) =>
        fetchAPI(`/pedidos/${encodeURIComponent(id)}/financeiro/status`, {
            method: 'POST',
            body: JSON.stringify(note ? { status, note } : { status }),
        }),
    // Triagem do caso (rmas:mediation:update): "Triagem concluída", nota opcional.
    triageCase: (rmaId, { note = null } = {}) =>
        fetchAPI(`/rmas/${encodeURIComponent(rmaId)}/triage`, {
            method: 'POST',
            body: JSON.stringify(note ? { note } : {}),
        }),
    finalizeCaseAttendance: (id, body) =>
        fetchAPI(`/rmas/${encodeURIComponent(id)}/attendance/finalize`, { method: 'POST', body: JSON.stringify(body) }),
    searchCaseBlingInvoices: (id, params = {}) =>
        fetchAPI(`/rmas/${encodeURIComponent(id)}/fiscal/bling?${BuildQuery(params)}`),
    previewCaseBlingInvoice: (id, invoiceId, params = {}) =>
        fetchAPI(`/rmas/${encodeURIComponent(id)}/fiscal/bling/${encodeURIComponent(invoiceId)}?${BuildQuery(params)}`),
    linkCaseBlingInvoice: (id, body) =>
        fetchAPI(`/rmas/${encodeURIComponent(id)}/fiscal/bling`, { method: 'POST', body: JSON.stringify(body) }),
    requestCaseLossInvoice: (id, body) =>
        fetchAPI(`/rmas/${encodeURIComponent(id)}/fiscal/loss-request`, { method: 'POST', body: JSON.stringify(body) }),
    // Decisão do supervisor (rmas:resolve): RESOLVIDO | NAO_RESOLVIDO |
    // APOIO_FINANCEIRO (este exige a nota com o que o financeiro deve verificar).
    reviewCase: (rmaId, { decision, note = null }) =>
        fetchAPI(`/rmas/${encodeURIComponent(rmaId)}/review`, {
            method: 'POST',
            body: JSON.stringify(note ? { decision, note } : { decision }),
        }),
    // Financeiro conclui o apoio pedido (rmas:finance:update) — resposta obrigatória.
    concludeFinanceSupport: (rmaId, { note }) =>
        fetchAPI(`/rmas/${encodeURIComponent(rmaId)}/finance-support/conclude`, {
            method: 'POST',
            body: JSON.stringify({ note }),
        }),

    // System (developer)
    getSystemJobs: () => fetchAPI('/system/jobs'),
    runSystemJobNow: (jobName, { resetSchedule = true } = {}) =>
        fetchAPI(`/system/jobs/${encodeURIComponent(jobName)}/run-now`, {
            method: 'POST',
            body: JSON.stringify({
                reset_schedule: Boolean(resetSchedule),
            }),
        }),
};

export { APIError, api, GetToken, RemoveToken, SetToken };
export default api;
